import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    if (checkedInput?.dataset.variantId) return checkedInput.dataset.variantId;

    // Color-expanded collection cards carry the exact variant they represent
    // on the grid-item wrapper. Fall back to that variant instead of the
    // product's selected_or_first_available_variant.
    const carrier = this.closest('[data-forced-variant-url][data-variant-id]');
    return carrier?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();
    this.#applyForcedVariantCard();
    this.#setupMobileCollectionSwipe();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);
    mediaQueryLarge.addEventListener('change', this.#setupMobileCollectionSwipe);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    mediaQueryLarge.removeEventListener('change', this.#setupMobileCollectionSwipe);
    this.#mobileCollectionSwipeController?.abort();
    this.#mobileCollectionSwipeController = null;
  }

  /**
   * Applies a server-selected variant to color-expanded grid cards.
   * Nested Horizon theme blocks only receive product context, so the static
   * product-card block passes the forced variant through data attributes and
   * this method aligns gallery/title/price links after render.
   */
  #applyForcedVariantCard() {
    const carrier = this.closest('[data-forced-variant-url]') || this;
    const forcedUrl = carrier.dataset.forcedVariantUrl;
    if (!forcedUrl) return;

    const forcedVariantId = carrier.dataset.variantId;

    // The quick-add snippet is rendered with product context only, so its
    // hidden variant input defaults to product.selected_or_first_available_variant.
    // For a color-expanded grid card, replace that value with the exact
    // variant represented by this card before any add-to-cart submission.
    if (forcedVariantId) {
      const variantInputs = this.querySelectorAll('quick-add-component product-form-component input[name="id"]');
      for (const input of variantInputs) {
        if (!(input instanceof HTMLInputElement)) continue;
        input.value = forcedVariantId;
        input.disabled = false;
        input.setAttribute('value', forcedVariantId);
      }

      const quickAdd = this.querySelector('quick-add-component');
      if (quickAdd instanceof HTMLElement) quickAdd.dataset.forcedVariantId = forcedVariantId;
    }

    const { productCardLink, cardGalleryLink, productTitleLink, slideshow } = this.refs;
    if (productCardLink instanceof HTMLAnchorElement) productCardLink.href = forcedUrl;
    if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = forcedUrl;
    if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = forcedUrl;

    for (const link of this.querySelectorAll('a[ref="cardGalleryLink"], a[ref="productTitleLink"]')) {
      if (link instanceof HTMLAnchorElement) link.href = forcedUrl;
    }

    const forcedImage = carrier.dataset.forcedVariantImage;
    const forcedMediaId = carrier.dataset.forcedVariantMediaId;
    const gallery = this.querySelector('[ref="cardGallery"]');
    const cardSwipeEnabled = gallery?.dataset.cardSwipeEnabled === 'true';

    if (gallery) {
      gallery.removeAttribute('on:pointerenter');
      gallery.removeAttribute('on:pointerleave');
      gallery.setAttribute('data-forced-variant-gallery', 'true');
    }

    if (forcedImage) this.setAttribute('data-featured-media-url', forcedImage);

    const masterSlides = Array.from(this.querySelectorAll('slideshow-slide')).filter(
      (slide) => slide instanceof HTMLElement
    );

    masterSlides.forEach((slide, index) => {
      slide.dataset.masterMediaIndex = String(index);
    });

    let startIndex = 0;
    if (forcedMediaId) {
      const foundIndex = masterSlides.findIndex((slide) => slide.getAttribute('slide-id') === forcedMediaId);
      if (foundIndex >= 0) startIndex = foundIndex;
    }

    let targetSlide = masterSlides[startIndex] || masterSlides[0] || null;

    // Color-expanded cards must initialize Horizon on the variant's actual media
    // index. Leaving the slideshow at its Liquid default of initial-slide="0"
    // causes desktop to reset every duplicated color card to product.media[0]
    // after this method has already selected the correct variant.
    if (slideshow && forcedMediaId && targetSlide) {
      // Horizon's variant-specific initial index is needed on desktop only.
      // Mobile collection cards build a local sliced gallery whose first item
      // is always index 0, so carrying the master-media index into mobile
      // makes Horizon fight the custom swipe controller.
      slideshow.setAttribute('initial-slide', isDesktopBreakpoint() ? String(startIndex) : '0');
    }

    if (cardSwipeEnabled && targetSlide) {
      // The section setting is the TOTAL number of images in this card's gallery.
      // Example: master media has 20 images, forced variant media is index 5,
      // setting is 4 => this card gets masterSlides.slice(5, 9), i.e. 5,6,7,8.
      const imageCount = Math.max(1, Math.min(6, Number(gallery?.dataset.cardSwipeAdditionalImages) || 2));
      let cardSlides = masterSlides.slice(startIndex, startIndex + imageCount);

      // If the forced media could not be found in the rendered master array,
      // preserve a stable first slide rather than wrapping back around.
      if (cardSlides.length === 0 && targetSlide) cardSlides = [targetSlide];

      const cardSlideSet = new Set(cardSlides);
      for (const slide of masterSlides) {
        const isInCardGallery = cardSlideSet.has(slide);
        slide.hidden = !isInCardGallery;
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', slide === cardSlides[0] ? 'false' : 'true');
      }

      gallery.dataset.cardMediaStartIndex = String(startIndex);
      gallery.dataset.cardMediaCount = String(cardSlides.length);

      // Keep Horizon aligned for desktop/fallback rendering, but do not reorder
      // either the DOM or Horizon's cached master array. Mobile collection swipe
      // is handled by the dedicated card-local track below.
      if (slideshow && forcedMediaId) {
        try {
          slideshow.select({ id: forcedMediaId }, undefined, { animate: false });
        } catch (_) {
          slideshow.current = startIndex;
        }
      }
    } else if (targetSlide) {
      // Swipe off: preserve the existing one-image-per-color behavior.
      for (const slide of masterSlides) {
        slide.hidden = slide !== targetSlide;
        slide.setAttribute('aria-hidden', slide === targetSlide ? 'false' : 'true');
      }

      if (slideshow && forcedMediaId) {
        try {
          slideshow.select({ id: forcedMediaId }, undefined, { animate: false });
        } catch (_) {
          // DOM visibility is the fallback.
        }
      }
    }

    // Horizon finishes its desktop slideshow setup asynchronously and can reset
    // current back to the old initial slide during that setup. Re-assert the
    // forced variant after initialization. Mobile collection cards use the
    // dedicated touch controller and are intentionally left alone here.
    if (slideshow && forcedMediaId && targetSlide && isDesktopBreakpoint()) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          try {
            slideshow.select({ id: forcedMediaId }, undefined, { animate: false });
          } catch (_) {
            // initial-slide above remains the fallback.
          }
        });
      });
    }

    // Rare fallback: if Shopify did not render the variant media slide at all,
    // use the first available slide shell for the variant image.
    if (targetSlide && forcedImage && forcedMediaId && targetSlide.getAttribute('slide-id') !== forcedMediaId) {
      const picture = targetSlide.querySelector('picture');
      picture?.querySelectorAll('source').forEach((source) => {
        source.removeAttribute('srcset');
        source.removeAttribute('sizes');
      });

      const image = targetSlide.querySelector('img');
      if (image instanceof HTMLImageElement) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = forcedImage;
        image.setAttribute('data-forced-variant-image', 'true');
      }
    }

    const color = carrier.dataset.forcedVariantColor?.trim();
    if (color) {
      const titleLink = this.querySelector('a[ref="productTitleLink"]');
      const titleNode =
        titleLink?.querySelector('[role="heading"]') ||
        titleLink?.querySelector('.title-text') ||
        titleLink?.querySelector('p, span');
      if (titleNode) {
        const baseTitle = titleNode.textContent?.trim() ?? '';
        if (baseTitle && !baseTitle.endsWith(`– ${color}`)) {
          titleNode.textContent = `${baseTitle} – ${color}`;
        }
      }
    }

    const forcedPrice = carrier.dataset.forcedVariantPrice;
    if (forcedPrice) {
      const price = this.querySelector('product-price [ref="priceContainer"] .price');
      if (price) price.textContent = forcedPrice;
    }
  }

  /** @type {AbortController | null} */
  #mobileCollectionSwipeController = null;

  /**
   * Uses a direct finger-following swipe interaction for product-card galleries
   * on mobile collection pages. Native iOS horizontal scrolling can keep
   * coasting after touchend; this implementation owns the horizontal gesture,
   * follows the finger 1:1, then snaps once on release with no momentum.
   */
  #setupMobileCollectionSwipe = () => {
    this.#mobileCollectionSwipeController?.abort();
    this.#mobileCollectionSwipeController = null;

    const gallery = this.querySelector('[ref="cardGallery"][data-card-swipe-enabled="true"]');
    const slideshow = this.refs.slideshow;
    const scroller = slideshow?.refs?.scroller || gallery?.querySelector('slideshow-slides');

    // Restore the normal Horizon structure when leaving the mobile breakpoint.
    const restoreDesktopStructure = () => {
      if (!(scroller instanceof HTMLElement)) return;
      const track = scroller.querySelector(':scope > .thugswim-mobile-card-track');
      if (track instanceof HTMLElement) {
        for (const slide of Array.from(track.children)) scroller.append(slide);
        track.remove();
      }

      const directSlides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide')).filter(
        (slide) => slide instanceof HTMLElement
      );
      directSlides
        .sort((a, b) => Number(a.dataset.masterMediaIndex || 0) - Number(b.dataset.masterMediaIndex || 0))
        .forEach((slide) => scroller.append(slide));

      scroller.style.removeProperty('overflow');
      scroller.style.removeProperty('touch-action');
      scroller.style.removeProperty('scroll-behavior');
      scroller.scrollLeft = 0;
      if (slideshow) slideshow.disabled = false;
    };

    if (mediaQueryLarge.matches) {
      restoreDesktopStructure();
      return;
    }

    const resultsList = this.closest('results-list[data-mobile-card-swipe="true"]');
    if (!resultsList) return;
    if (!(gallery instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    // On collection cards we deliberately bypass Horizon's Scroller/index logic.
    // #applyForcedVariantCard has already selected a contiguous slice of the
    // master product-media array. This local track contains only that slice.
    let track = scroller.querySelector(':scope > .thugswim-mobile-card-track');
    if (!(track instanceof HTMLElement)) {
      track = document.createElement('div');
      track.className = 'thugswim-mobile-card-track';
      track.setAttribute('aria-live', 'polite');

      // Do not trust Horizon's current hidden/visible state here. On non-first
      // color cards Horizon may have already applied its desktop/master index
      // before this mobile controller runs. Rebuild the mobile gallery directly
      // from the immutable master media indices saved by #applyForcedVariantCard.
      const allSlides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide')).filter(
        (slide) => slide instanceof HTMLElement
      );
      const sliceStart = Math.max(0, Number(gallery.dataset.cardMediaStartIndex) || 0);
      const sliceCount = Math.max(1, Number(gallery.dataset.cardMediaCount) || 1);
      const sliceEnd = sliceStart + sliceCount;

      const mobileSlides = allSlides
        .filter((slide) => {
          const masterIndex = Number(slide.dataset.masterMediaIndex);
          return Number.isFinite(masterIndex) && masterIndex >= sliceStart && masterIndex < sliceEnd;
        })
        .sort((a, b) => Number(a.dataset.masterMediaIndex) - Number(b.dataset.masterMediaIndex));

      // Fallback to the first available slide only if the stored slice cannot be
      // reconstructed. Never wrap around to product.media[0] for a valid slice.
      const slidesForTrack = mobileSlides.length ? mobileSlides : allSlides.slice(0, 1);

      for (const slide of allSlides) {
        const inMobileSlice = slidesForTrack.includes(slide);
        slide.hidden = !inMobileSlice;
        slide.setAttribute('aria-hidden', slide === slidesForTrack[0] ? 'false' : 'true');
      }

      for (const slide of slidesForTrack) track.append(slide);
      scroller.prepend(track);
    }

    const getSlides = () =>
      Array.from(track.children).filter(
        (slide) => slide instanceof HTMLElement && slide.matches('slideshow-slide:not([hidden])')
      );

    const slides = getSlides();
    if (slides.length <= 1) return;

    gallery.setAttribute('data-mobile-collection-swipe-ready', 'true');
    if (slideshow) slideshow.disabled = true;

    scroller.scrollLeft = 0;
    scroller.style.overflow = 'hidden';
    scroller.style.scrollBehavior = 'auto';
    scroller.style.touchAction = 'pan-y pinch-zoom';

    Object.assign(track.style, {
      display: 'flex',
      width: '100%',
      willChange: 'transform',
      transform: 'translate3d(0px, 0, 0)',
      transition: 'none',
    });

    slides.forEach((slide, index) => {
      slide.style.flex = '0 0 100%';
      slide.style.minWidth = '100%';
      slide.removeAttribute('reveal');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });

    const controller = new AbortController();
    const { signal } = controller;
    this.#mobileCollectionSwipeController = controller;

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startIndex = 0;
    let currentIndex = 0;
    let axis = null;
    let didSwipe = false;
    let suppressClickUntil = 0;

    const width = () => Math.max(1, scroller.clientWidth);

    const renderPosition = (index, dragPx = 0, animate = false) => {
      const clamped = Math.max(0, Math.min(index, getSlides().length - 1));
      track.style.transition = animate ? 'transform 180ms ease-out' : 'none';
      track.style.transform = `translate3d(${-(clamped * width()) + dragPx}px, 0, 0)`;
    };

    const commitIndex = (index, animate = true) => {
      const currentSlides = getSlides();
      currentIndex = Math.max(0, Math.min(index, currentSlides.length - 1));
      renderPosition(currentIndex, 0, animate);
      currentSlides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === currentIndex ? 'false' : 'true');
      });
    };

    const resetGesture = () => {
      pointerId = null;
      axis = null;
      didSwipe = false;
      gallery.removeAttribute('data-mobile-card-dragging');
    };

    const onPointerDown = (event) => {
      if (!event.isPrimary || event.pointerType === 'mouse' || pointerId !== null) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startIndex = currentIndex;
      axis = null;
      didSwipe = false;
      track.style.transition = 'none';
    };

    const onPointerMove = (event) => {
      if (event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (axis === null) {
        if (Math.max(absX, absY) < 7) return;
        axis = absX > absY * 1.08 ? 'horizontal' : 'vertical';
        if (axis === 'vertical') return;
        gallery.setAttribute('data-mobile-card-dragging', 'true');
        try { scroller.setPointerCapture(event.pointerId); } catch (_) {}
      }

      if (axis !== 'horizontal') return;
      if (event.cancelable) event.preventDefault();

      didSwipe ||= absX > 9;

      let dragX = deltaX;
      const lastIndex = getSlides().length - 1;
      if ((startIndex === 0 && deltaX > 0) || (startIndex === lastIndex && deltaX < 0)) {
        dragX *= 0.28;
      }

      renderPosition(startIndex, dragX, false);
    };

    const endGesture = (event) => {
      if (event.pointerId !== pointerId) return;

      if (axis === 'horizontal') {
        const deltaX = event.clientX - startX;
        const threshold = Math.min(58, Math.max(30, width() * 0.14));
        let target = startIndex;
        if (Math.abs(deltaX) >= threshold) target += deltaX < 0 ? 1 : -1;
        commitIndex(target, true);

        if (didSwipe) suppressClickUntil = performance.now() + 350;
        try {
          if (scroller.hasPointerCapture(event.pointerId)) scroller.releasePointerCapture(event.pointerId);
        } catch (_) {}
      }

      resetGesture();
    };

    const suppressSwipeClick = (event) => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    scroller.addEventListener('pointerdown', onPointerDown, { signal });
    scroller.addEventListener('pointermove', onPointerMove, { signal });
    scroller.addEventListener('pointerup', endGesture, { signal });
    scroller.addEventListener('pointercancel', endGesture, { signal });
    this.addEventListener('click', suppressSwipeClick, { capture: true, signal });

    const onResize = () => commitIndex(currentIndex, false);
    window.addEventListener('resize', onResize, { signal });

    // The selected variant's contiguous media slice is already the only visible
    // set. Start at its first item and never consult Horizon's slideshow index.
    requestAnimationFrame(() => commitIndex(0, false));
  };

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
